import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Check, FileArchive, FileSpreadsheet, RefreshCw } from "lucide-react";
import { EASE } from "@/lib/motion";
import { StatusPill } from "@/components/product/GlassUI";
import HeroWorkforce from "@/components/product/HeroWorkforce";

/**
 * Hero product workspace (3 states).
 * Structural refs:
 * - Month End → MonthEndReports.jsx
 * - Workforce → workforce-mini.js (approved HeroWorkforce)
 * - Migration → PastelMigration.jsx / pastel_migration.py
 */

const TABS = [
  { id: "month-end", label: "Month End" },
  { id: "workforce", label: "Workforce" },
  { id: "migration", label: "Migration Tool" },
];

const CYCLE_MS = 5500;
const IDLE_MS = 10000;

const panelMotion = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.36, ease: EASE },
};

function MonthEndState() {
  // Structure from MonthEndReports.jsx - pack configuration screen
  const companies = [
    { name: "Northgate Retail", id: "NGR-001", on: true },
    { name: "Helix Manufacturing", id: "HLX-004", on: true },
    { name: "Aurora Logistics", id: "AUR-009", on: true },
  ];
  const reports = [
    { name: "Monthly Analysis", on: true },
    { name: "EMP201 Reports", on: true },
    { name: "Recon Reports", on: true },
    { name: "Payslips", on: true },
    { name: "Net Payment Listing", on: true },
    { name: "Month End Pack Summary", on: true },
  ];

  return (
    <div className="flex flex-col gap-2" data-testid="hero-ws-month-end">
      <div className="shrink-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-teal">Payroll operations</p>
        <p className="text-[14px] font-medium leading-snug text-slate-100 sm:text-[15px]">Month End Payroll Reports</p>
        <p className="text-[11px] text-slate-500">Multi-company pack for the selected period</p>
      </div>

      <div className="grid shrink-0 grid-cols-3 gap-1.5 rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-2">
        {[
          ["Year", "2026"],
          ["Month", "August"],
          ["Company group", "Retail Group"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] text-slate-500 sm:text-[11px]">{k}</p>
            <p className="truncate text-[12px] font-medium text-slate-100 sm:text-[13px]">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-2">
        <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-2">
          <p className="text-[12px] font-medium text-slate-300">Companies in group</p>
          <p className="text-[10px] text-slate-500">3 selected</p>
          <ul className="mt-1.5 space-y-1">
            {companies.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-[12px]">
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border border-teal/50 bg-teal/20">
                  <Check className="h-2.5 w-2.5 text-teal" aria-hidden="true" />
                </span>
                <span className="truncate text-slate-200">{c.name}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-2">
          <p className="text-[12px] font-medium text-slate-300">Reports to include</p>
          <p className="text-[10px] text-slate-500">6 selected</p>
          <ul className="mt-1.5 space-y-1">
            {reports.map((r) => (
              <li key={r.name} className="flex items-center gap-2 text-[12px]">
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border border-teal/50 bg-teal/20">
                  <Check className="h-2.5 w-2.5 text-teal" aria-hidden="true" />
                </span>
                <span className="truncate text-slate-200">{r.name}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="shrink-0 rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-2">
        <p className="text-[11px] text-slate-500">Pack type</p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <p className="flex items-center gap-2 text-[12px] text-teal">
            <span className="h-2 w-2 rounded-full bg-teal" aria-hidden="true" />
            Group Pack
          </p>
          <p className="flex items-center gap-2 text-[12px] text-slate-500">
            <span className="h-2 w-2 rounded-full border border-white/20" aria-hidden="true" />
            Individual Company ZIPs
          </p>
        </div>
      </div>

      <div className="shrink-0 space-y-1.5">
        <div className="flex items-center justify-between rounded-lg border border-white/[0.07] bg-white/[0.03] px-3 py-1.5 text-[12px]">
          <span className="text-slate-400">
            <span className="font-medium text-slate-100">3</span> companies ·{" "}
            <span className="font-medium text-slate-100">18</span> reports
          </span>
          <StatusPill>Ready to pack</StatusPill>
        </div>
        <button
          type="button"
          className="flex w-full items-center justify-center rounded-xl border border-teal/35 bg-teal/[0.12] px-3 py-2 text-[13px] font-medium text-teal transition-colors hover:bg-teal/[0.18] sm:text-[14px]"
        >
          Review and generate
        </button>
      </div>
    </div>
  );
}

function MigrationState({ reduced }) {
  // Terms from PastelMigration.jsx - pack of PaySpace templates from Pastel backup
  const steps = [
    { n: "01", title: "Import backup", detail: "Sage Pastel Payroll .zip" },
    { n: "02", title: "Convert", detail: "Company · employees · components" },
    { n: "03", title: "Generate templates", detail: "PaySpace import templates" },
    { n: "04", title: "Migration pack ready", detail: "Download · upload to PaySpace" },
  ];

  return (
    <div className="flex flex-col gap-2" data-testid="hero-ws-migration">
      <div className="shrink-0">
        <p className="text-[14px] font-medium leading-snug text-slate-100 sm:text-[15px]">Pastel → PaySpace Migration</p>
        <p className="mt-0.5 text-[12px] leading-snug text-slate-400 sm:text-[13px]">
          Upload your Sage Pastel Payroll backup. RORAQ converts the data into PaySpace-ready import
          templates.
        </p>
      </div>

      <div className="grid shrink-0 grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-1 sm:gap-1.5">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-1.5 py-2 text-center sm:px-2">
          <FileArchive className="mx-auto h-4 w-4 text-slate-300" aria-hidden="true" />
          <p className="mt-1 text-[10px] font-medium leading-snug text-slate-100 sm:text-[11px]">Sage Pastel backup</p>
        </div>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-teal/80" aria-hidden="true" />
        <div className="rounded-xl border border-teal/25 bg-teal/[0.06] px-1.5 py-2 text-center sm:px-2">
          <RefreshCw className="mx-auto h-4 w-4 text-teal" aria-hidden="true" />
          <p className="mt-1 text-[10px] font-medium leading-snug text-teal sm:text-[11px]">RORAQ conversion</p>
        </div>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-teal/80" aria-hidden="true" />
        <div className="rounded-xl border border-teal/30 bg-teal/[0.1] px-1.5 py-2 text-center sm:px-2">
          <FileSpreadsheet className="mx-auto h-4 w-4 text-teal" aria-hidden="true" />
          <p className="mt-1 text-[10px] font-medium leading-snug text-teal sm:text-[11px]">PaySpace templates</p>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-1.5">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={reduced ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduced ? 0 : 0.05 + i * 0.05, duration: 0.28, ease: EASE }}
            className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-2"
          >
            <p className="font-mono text-[10px] text-teal">{s.n}</p>
            <p className="text-[12px] font-medium leading-snug text-slate-100 sm:text-[13px]">{s.title}</p>
            <p className="text-[10px] leading-snug text-slate-500 sm:text-[11px]">{s.detail}</p>
          </motion.div>
        ))}
      </div>

      <div className="flex shrink-0 items-center justify-between rounded-xl border border-teal/30 bg-teal/[0.1] px-3 py-2.5">
        <div>
          <p className="text-[11px] text-teal/80">Conversion progress</p>
          <p className="text-[13px] font-medium text-teal sm:text-[14px]">Migration pack ready</p>
        </div>
        <StatusPill>Ready</StatusPill>
      </div>
    </div>
  );
}

function StateBody({ tab, reduced }) {
  switch (tab) {
    case "workforce":
      return <HeroWorkforce />;
    case "migration":
      return <MigrationState reduced={reduced} />;
    default:
      return <MonthEndState />;
  }
}

export default function HeroWorkspace({ className = "" }) {
  const reduced = useReducedMotion();
  const [tab, setTab] = useState("month-end");
  const [pointerInside, setPointerInside] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const idxRef = useRef(0);
  const hasInteractedRef = useRef(false);
  const lastInteractionRef = useRef(0);
  const rootRef = useRef(null);

  const markInteraction = useCallback(() => {
    hasInteractedRef.current = true;
    lastInteractionRef.current = Date.now();
  }, []);

  const selectTab = useCallback(
    (id) => {
      markInteraction();
      idxRef.current = Math.max(0, TABS.findIndex((t) => t.id === id));
      setTab(id);
    },
    [markInteraction]
  );

  useEffect(() => {
    if (reduced) return undefined;
    const id = window.setInterval(() => {
      if (pointerInside || focusInside) return;
      if (hasInteractedRef.current && Date.now() - lastInteractionRef.current < IDLE_MS) return;
      idxRef.current = (idxRef.current + 1) % TABS.length;
      setTab(TABS[idxRef.current].id);
    }, CYCLE_MS);
    return () => window.clearInterval(id);
  }, [reduced, pointerInside, focusInside]);

  const onFocusCapture = (e) => {
    if (rootRef.current?.contains(e.target)) {
      setFocusInside(true);
      markInteraction();
    }
  };

  const onBlurCapture = (e) => {
    const next = e.relatedTarget;
    if (!rootRef.current?.contains(next)) {
      setFocusInside(false);
      lastInteractionRef.current = Date.now();
    }
  };

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      data-testid="hero-workspace"
      onPointerEnter={() => setPointerInside(true)}
      onPointerLeave={() => {
        setPointerInside(false);
        lastInteractionRef.current = Date.now();
      }}
      onPointerDown={markInteraction}
      onClick={markInteraction}
      onKeyDown={markInteraction}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}
    >
      {/* Fixed outer viewport - same size every tab */}
      <div className="glass relative flex h-[530px] flex-col overflow-hidden rounded-2xl shadow-[0_28px_80px_rgba(0,0,0,0.55)] sm:h-[550px] lg:h-[570px]">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/[0.07] px-3.5 py-2 sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <img
              src="/brand/lockup-white.png"
              alt=""
              aria-hidden="true"
              className="h-5 w-auto shrink-0 sm:h-6"
              draggable={false}
            />
            <span className="hidden h-3 w-px bg-white/10 sm:block" aria-hidden="true" />
            <span className="hidden items-center gap-1.5 truncate text-[12px] text-slate-400 sm:inline-flex">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" />
              Northgate Retail
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <StatusPill>Live</StatusPill>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-[11px] text-slate-500">
              Demo data
            </span>
          </div>
        </div>

        <div
          className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-white/[0.06] px-2.5 py-2 sm:gap-2 sm:px-4"
          role="tablist"
          aria-label="RORAQ product views"
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`hero-ws-tab-${t.id}`}
                onClick={() => selectTab(t.id)}
                onMouseEnter={markInteraction}
                className={`relative shrink-0 rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 sm:px-5 sm:text-[14px] ${
                  active ? "text-teal" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {t.label}
                {active ? (
                  <motion.span
                    layoutId="hero-ws-tab-underline"
                    className="absolute inset-x-3 -bottom-0.5 h-px bg-teal/70"
                    transition={{ duration: 0.3, ease: EASE }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden px-3.5 pb-3.5 pt-2.5 sm:px-5 sm:pb-4 sm:pt-3">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={tab}
              role="tabpanel"
              className="h-full"
              {...(reduced
                ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.15 } }
                : panelMotion)}
            >
              <StateBody tab={tab} reduced={!!reduced} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
